/**
 * Liang — Root Application Component
 * Handles routing between Login, Chat, and Progress pages
 */
import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import { API_URL } from './config';
// Bundle de-bloat (audit r3): import Spinner by its DIRECT path, not the
// `./components/ui` barrel. App.js is the EAGER entry module; pulling Spinner
// through the barrel made the whole barrel reachable from the entry graph,
// which (before the barrel + `sideEffects` fix) dragged mermaid/react-markdown
// into the main chunk. A direct import keeps the entry chunk to just Spinner.
import Spinner from './components/ui/Spinner';
import Login from './Login';
import { resetAnalyticsCache } from './utils/analyticsCache';
import { clearInterviewCache } from './career/interview/interviewCache';
import { readStoredAuth } from './utils/readStoredAuth';
import { isAccessTokenExpired } from './utils/accessTokenExpiry';
import { carriesOwnCredential, refreshAccessTokenAtBoot } from './setupAxiosInterceptor';
import { triggerLoginPrewarm } from './utils/triggerLoginPrewarm';
import { triggerAnalyticsPrefetch } from './utils/triggerAnalyticsPrefetch';
// ANL-PR3: the foreground engagement heartbeat (mounted once for authenticated
// users) + the subject-hint reset on logout. The beacon accumulates active
// (visible+focused) time and flushes it to /telemetry/session tagged with the
// current route's engagement-area; it is best-effort, silent, and self-disables
// after a flag-OFF 404 / under-13 403. clearAreaSubjectHints wipes the learn/prep
// subject labels so a new user on a shared device starts clean.
import { useEngagementBeacon } from './telemetry/useEngagementBeacon';
import { clearAreaSubjectHints } from './telemetry/areaForPath';
// R1.1: the exam autosave outbox persists a student's un-sent answers in
// localStorage. On logout (shared-device boundary) we purge EVERY exam outbox so
// the next user on the device can never inherit a minor's queued responses.
import { purgeAllExamOutboxes } from './prep/exam/examOutbox';
import { purgeAssessmentDrafts } from './career/assessmentDraft';
// POST-AIQ-151 COPPA-PR4: the global age/consent backbone. The interceptor
// turns a 426 age_required into a transparent DOB-capture+retry, and a 403
// platform_minor consent_required into the under-13 limited-mode overlay, on
// EVERY gated route. AgeConsentGate is the mounted host that renders those.
import { installAgeConsentInterceptor } from './coppa/ageConsentInterceptor';
import AgeConsentGate from './coppa/AgeConsentGate';
// F14-PR3: the billing/entitlement backbone, mirroring the COPPA gate above
// exactly. installBillingUpgradeInterceptor turns a 402 entitlement_required
// (from ANY gated route — exam-start, tailored-practice, and F13-PR4's
// study-plan GET the moment it lands, with zero extra wiring) into the
// shared <UpgradeDialog>, mounted via <UpgradeDialogHost>. <BillingProvider>
// is the useBilling() source every billing surface reads (kept eager +
// small, like the interceptor/host — same "structural backbone, not
// content" reasoning as the COPPA gate; the CONTENT it gates (UpgradeDialog,
// PremiumHub, ActivationPage) stays behind normal component/route
// boundaries). BillingGatedRoute is the route-level dark guard for the two
// new billing pages below.
import { installBillingUpgradeInterceptor } from './billing/billingUpgradeInterceptor';
import { BillingProvider } from './billing/BillingContext';
import UpgradeDialogHost from './billing/UpgradeDialogHost';
import BillingGatedRoute from './billing/BillingGatedRoute';
// The sessionStorage KEY only (not the lazy ActivationPage.js's own
// clearPendingIntentId helper — importing FROM a lazy route chunk into this
// eager entry module would drag it into the eager bundle). CheckoutFlow.js
// is already part of the eager graph (UpgradeDialogHost -> UpgradeDialog ->
// CheckoutFlow), so this adds no new eager dependency.
import { PENDING_INTENT_KEY } from './billing/CheckoutFlow';
// Shell components extracted from this file (App.js shell split). Their bodies
// are unchanged; App.js now composes them. All three are leaf components with
// no back-dependency on App — the `_prefetch*` route-chunk warmers that NavBar
// and handleLogin both need live in their own `./routePrefetch` leaf module, so
// the graph stays acyclic (no App↔NavBar import cycle).
import NavBar from './components/NavBar';
import VerificationBanner from './components/VerificationBanner';
import ErrorBoundary from './components/ErrorBoundary';
// R2.2: per-route crash guard — wraps EVERY lazy route element below so a
// render error in one route's subtree can't blank the whole shell. See
// that file's header for why this is separate from the 3 existing
// branch-level <ErrorBoundary> mounts (Onboarding / AuthenticatedApp /
// logged-out shell), which are unchanged and still the outermost net.
import RouteErrorBoundary from './components/RouteErrorBoundary';
// handleLogin warms the post-auth student/teacher route subset; the full set of
// warmers (used on NavBar hover too) is declared once in ./routePrefetch.
import {
  _prefetchChat,
  _prefetchLearningPaths,
  _prefetchExerciseLayout,
  _prefetchClassrooms,
  _prefetchRichText,
} from './routePrefetch';
import './styles/liang-brand.css';
import './App.css';

// Reliability layer (2026-04-25 reliability fix): every transient
// network or 5xx failure used to leave the user staring at a dead
// loading spinner with no recovery. Now we auto-retry up to 3 times
// with exponential backoff. Applied to the global axios instance so
// every request — including question fetches and prefetches —
// inherits it. Idempotent reads (GET) and idempotent writes
// (/generate-question, /warm-questions, both repeatable on the
// server) are safe to retry.
//
// MUST live below all imports (CRA enforces ESLint `import/first`
// at production build time, and a top-level call between imports
// fails the prod build — caught 2026-04-25 in Railway). The retry
// config is global, so it just needs to run before any axios call;
// running here at module-init time satisfies that.
axiosRetry(axios, {
  retries: 3,
  retryDelay: (retryCount) => {
    // Exponential backoff: 250ms, 500ms, 1000ms — capped before
    // the user perceives the wait as broken.
    return Math.min(250 * Math.pow(2, retryCount - 1), 1500);
  },
  retryCondition: (error) => {
    // Retry on:
    //   - network errors (no response received)
    //   - 5xx server errors (overloaded backend, transient AI failures)
    //   - 408 Request Timeout
    //   - 429 Too Many Requests (rate-limited; backoff helps)
    // Skip 4xx client errors (auth, validation) — retrying won't help
    // and would just amplify the bug.
    if (axiosRetry.isNetworkOrIdempotentRequestError(error)) return true;
    const status = error.response?.status;
    return status === 503 || status === 408 || status === 429 || status === 502 || status === 504;
  },
  shouldResetTimeout: true,
});

// Route-level code splitting (perf #13/15 expansion 2026-04-25):
// previously only the in-app routes were lazy. Login was eager because
// it's needed on first paint, but the auth-flow secondary screens
// (ResetPassword, VerifyEmail) and the post-signup Onboarding modal
// only render on rare paths — they shouldn't bloat the initial bundle.
//
// `webpackChunkName` magic comments give the chunks identifiable
// names in DevTools / Network panel for debugging perf regressions.
const Chat = React.lazy(() =>
  import(/* webpackChunkName: "route-chat" */ './Chat'));
const Progress = React.lazy(() =>
  import(/* webpackChunkName: "route-progress" */ './Progress'));
const LearningPaths = React.lazy(() =>
  import(/* webpackChunkName: "route-paths" */ './LearningPaths'));
const ExerciseLayout = React.lazy(() =>
  import(/* webpackChunkName: "route-exercise" */ './ExerciseLayout'));
// F11 PR2: the Daily Review session runner — a lightweight queue driver
// (due/weak/gap concepts), reached via the "Start review" CTA on
// RecommendedPractice/Progress or a direct /review deep link. Lazy like every
// other in-app route; only a student who actually opens a review session
// pays the chunk bytes.
const ReviewSession = React.lazy(() =>
  import(/* webpackChunkName: "route-review" */ './ReviewSession'));
// CM-REFERENCE (concept refreshers, PRD §D7): the standalone concept-
// refresher card, reached from the prep due-list, a learning-path node's
// "Related refresher" pill, a sibling link, or a direct deep link. Lazy like
// every other in-app route — only a student who opens a refresher pays the
// chunk bytes.
const ReferencePage = React.lazy(() =>
  import(/* webpackChunkName: "route-reference" */ './ReferencePage'));
const Classrooms = React.lazy(() =>
  import(/* webpackChunkName: "route-classrooms" */ './Classrooms'));
// Auth-flow secondary screens — only on /reset-password and
// /verify-email URLs, both reached from emailed links (rare).
const ResetPassword = React.lazy(() =>
  import(/* webpackChunkName: "auth-reset-password" */ './ResetPassword'));
const VerifyEmail = React.lazy(() =>
  import(/* webpackChunkName: "auth-verify-email" */ './VerifyEmail'));
// Onboarding only renders for brand-new registrations; existing
// users never hit this code path.
const Onboarding = React.lazy(() =>
  import(/* webpackChunkName: "onboarding" */ './Onboarding'));
// Task #60 (2026-06-13): public privacy policy. PUBLIC (no-auth) route —
// declared in the logged-out <Routes> below alongside ResetPassword /
// VerifyEmail so a logged-out visitor (and the register-page "Privacy Policy"
// link) can reach it. Lazy-loaded — a one-off legal page should never sit in
// the first-paint bundle.
const PrivacyPolicy = React.lazy(() =>
  import(/* webpackChunkName: "privacy-policy" */ './PrivacyPolicy'));
// F12 PR2b: the public guardian-nudge unsubscribe landing page (the page a
// guardian's email link opens). PUBLIC (no-auth, token-in-fragment) — declared
// alongside PrivacyPolicy in all three route mounts below (dual-mode
// `standalone` prop, same mechanism) so the link resolves whether or not the
// visitor happens to also be signed in. Lazy-loaded — only a guardian who
// actually clicks an unsubscribe link pays the chunk bytes.
const UnsubscribePage = React.lazy(() =>
  import(/* webpackChunkName: "guardian-unsubscribe" */ './guardian/UnsubscribePage'));
// POST-AIQ-122 (2026-05-21): Prep Path — second top-level product.
// Lazy-loaded because students who never visit /prep shouldn't pay
// the bytes. AdminPrep is even rarer; its chunk only loads for the
// handful of operators with user.is_admin === true.
const Prep = React.lazy(() =>
  import(/* webpackChunkName: "route-prep" */ './Prep'));
const PrepPath = React.lazy(() =>
  import(/* webpackChunkName: "route-prep-path" */ './PrepPath'));
// DSAT-PR6: the full-length Digital-SAT exam shell. A full-screen focus-mode
// takeover (its own header + timer), lazy-loaded as its own chunk — only a
// student who actually starts a full practice test pays the bytes, mirroring how
// PrepPath sits behind its own chunk. Dark behind the backend flag (a 404 on the
// state fetch degrades to a warm "not available" card, so the deep link is safe).
const ExamShell = React.lazy(() =>
  import(/* webpackChunkName: "route-prep-exam" */ './prep/exam/ExamShell'));
// F10 PR4b: the "practice what you missed" runner the score-report CTA
// navigates to. Its own chunk (a student who never completes a full exam
// never pays for it), lazy like every other Prep sub-surface.
const PracticeSetRunner = React.lazy(() =>
  import(/* webpackChunkName: "route-prep-practice-set" */ './prep/PracticeSetRunner'));
const AdminPrep = React.lazy(() =>
  import(/* webpackChunkName: "route-admin-prep" */ './AdminPrep'));
// POST-AIQ-126 (PR 5): student personal upload — private to uploader.
const StudentPrepUpload = React.lazy(() =>
  import(/* webpackChunkName: "route-prep-upload" */ './StudentPrepUpload'));
// POST-AIQ-143 PR 7 (2026-05-29): Admissions Advisor — third top-level
// product (chat + shortlist + profile + guardian consent). Lazy-loaded as
// ONE routed section (AdvisorSection owns the /advisor/* sub-routes) so the
// bytes only download for students/guardians who open it. Mirrors how Prep
// is gated behind its own chunk.
const AdvisorSection = React.lazy(() =>
  import(/* webpackChunkName: "route-advisor" */ './admissions/AdvisorSection'));
// POST-AIQ-150 CD-PR7 (2026-05-31): Career Discovery — self-serve career
// matching (tiered consent + profile + accessible Likert assessments + ranked
// O*NET matches + insights + what-if explorer + shortlist). Lazy-loaded as ONE
// routed section (CareerSection owns the /career/* sub-routes) so the bytes
// only download for students who open it. Mirrors how Advisor/Prep are gated
// behind their own chunks. (The advisor chat sub-surface is a deferred CD-PR6
// placeholder inside the section.)
const CareerSection = React.lazy(() =>
  import(/* webpackChunkName: "route-career" */ './career/CareerSection'));
// POST-AIQ-134 PR 5 (2026-05-28): teacher + admin analytics dashboards.
// Lazy-loaded because most students will never load these chunks; the
// teacher and admin populations are smaller than the student base.
const TeacherDashboardPage = React.lazy(() =>
  import(/* webpackChunkName: "route-teacher-dashboard" */ './pages/TeacherDashboard'));
const AdminDashboardPage = React.lazy(() =>
  import(/* webpackChunkName: "route-admin-dashboard" */ './pages/AdminDashboard'));
// ANL-PR4 (analytics epic capstone): the FOUNDER KPI dashboard (platform-wide,
// admin-only). Lazy-loaded — only the handful of platform admins ever load this
// chunk, mirroring how AdminDashboard / AdminPrep sit behind their own chunks.
const FounderDashboardPage = React.lazy(() =>
  import(/* webpackChunkName: "route-founder-dashboard" */ './pages/FounderDashboard'));
// POST-AIQ-151 COPPA-PR4: the teacher "Career sign-off" dashboard (approve /
// decline students' Career Discovery requests). Lazy-loaded — only teachers
// who open it pay the bytes. Mirrors the Career section's own chunk.
const TeacherCareerSignoff = React.lazy(() =>
  import(/* webpackChunkName: "route-career-signoff" */ './career/TeacherCareerSignoff'));
// Task #47 GE-PR4 (2026-06-12): Guardian Experience — the family home (child
// picker + growth summary + admissions + access toggles). Lazy-loaded — only
// guardians (user.is_guardian) ever open /family, so the bytes are gated behind
// this chunk, mirroring how Advisor/Career sit behind their own chunks.
const GuardianHome = React.lazy(() =>
  import(/* webpackChunkName: "route-guardian" */ './guardian/GuardianHome'));
// 2026-10-10: the landing page for the student→guardian invite email
// (`/guardian/accept#token=…`, minted by guardian_linking.py `invite_guardian`).
// Its own tiny chunk: it's reached cold from an email, never from in-app nav,
// so warming it with the Family chunk would be wasted bytes for every guardian
// who never opens an invite. Registered in BOTH role branches below (like
// /privacy and the unsubscribe route) — a guardian may also be a teacher — and
// for NON-guardians too, so a student who opens a forwarded invite gets an
// explainer rather than a silent redirect home (every dead end is a bug).
const GuardianAcceptPage = React.lazy(() =>
  import(/* webpackChunkName: "route-guardian-accept" */ './guardian/GuardianAcceptPage'));
// F14-PR3: the Premium landing page + the post-checkout activation poll.
// Lazy like every other product surface — only a student/guardian who
// actually opens the billing surface pays the chunk bytes. Both routes are
// registered TWICE below (teacher branch + student branch), mirroring
// /privacy and /guardian/nudges/unsubscribe's exact dual-registration
// reasoning: Stripe's STATIC checkout-cancel/success/portal-return redirect
// paths (config.py, both under /prep/premium) must resolve for a signed-in
// guardian regardless of whether their account also has is_teacher=true.
const PremiumHub = React.lazy(() =>
  import(/* webpackChunkName: "route-billing-premium" */ './billing/PremiumHub'));
const ActivationPage = React.lazy(() =>
  import(/* webpackChunkName: "route-billing-activation" */ './billing/ActivationPage'));

// Route-chunk prefetchers (hover/focus warming + post-login warming) live in
// ./routePrefetch — a leaf both App.js and NavBar import, so the module graph
// stays acyclic. See that file's header for the same-chunk-name contract.

// warm(fn) — fire a fire-and-forget prefetch and SWALLOW its rejection.
// The post-login warmers are pure `() => import(...)` chunk preloads; a
// rejected dynamic import (a mid-deploy rehash, a dropped request) with no
// `.catch` surfaces as an unhandled promise rejection — console noise in dev,
// and on some setups a window `unhandledrejection` that error-reporting picks
// up as a phantom crash. These loads are best-effort ("warm the cache so the
// first navigation is instant"); a miss is silently fine — the on-navigate
// React.lazy import (and, for RichText, its in-wrapper retry boundary) is the
// real load and owns its own failure UX. One tiny shared helper keeps the
// whole fan-out quiet without a `.catch(() => {})` on every line.
const warm = (fn) => {
  try {
    Promise.resolve(fn()).catch(() => {});
  } catch {
    // A synchronous throw from the warmer (shouldn't happen for a bare
    // `import()`) must not break the login flow either.
  }
};

function AuthenticatedApp({ user, onLogout, onUpdateUser }) {
  const isTeacher = user?.is_teacher === true;
  // POST-AIQ-122: admin-only routes (currently /admin/prep) gate on the
  // is_admin flag from the login payload. Backend enforces independently.
  const isAdmin = user?.is_admin === true;
  // Task #47 GE-PR4: is_guardian is the guardian ROLE flag (set at
  // registration, auth.py `req.role == "guardian"`), shipped on the
  // login/refresh payload — NOT derived from holding a guardian link. The
  // /family route is declared for any guardian (teacher or student account).
  // The verified-link + consent gates enforce server-side; this is the UX route
  // gate. Declared in both nav blocks so a guardian-teacher and a
  // guardian-parent both resolve /family.
  const isGuardian = user?.is_guardian === true;
  // Issue #56: per-user hide-section UX preference. When a section is hidden we
  // still DECLARE its route (so a deep link / bookmark resolves) but render a
  // redirect home instead of the section — a hidden section must not dead-end.
  // DISPLAY-ONLY: this never substitutes for the server-side consent gates.
  const hidden = user?.hidden_sections || {};

  // NOTE: the Authorization header used to be installed here as a side
  // effect of this component mounting (`useEffect(() => { axios.defaults
  // .headers.common['Authorization'] = ... }, [token])`). That approach had
  // a cold-boot race: React commits CHILD effects before PARENT effects, so
  // a routed child firing a request from its OWN mount effect (e.g.
  // ExerciseLayout's loadPaths()) could dispatch BEFORE this effect ran,
  // going out with no Authorization header → a spurious 401. The header is
  // now attached by a REQUEST interceptor (setupAxiosAuthHeader.js,
  // installed once at module-init in index.js) that reads the token from
  // localStorage on every request — independent of any component's mount
  // timing. See setupAxiosAuthHeader.js for the full writeup.

  // ANL-PR3: mount the foreground engagement heartbeat here — inside the router
  // (it needs useLocation) and ONLY for authenticated users (this component
  // renders only when token+user are present). It installs one set of
  // visibility/focus/unload listeners + a 5s check interval, flushes ~45s of
  // ACTIVE time to /telemetry/session tagged with the current route's area, and
  // self-disables after a flag-OFF 404 / under-13 403. A stale-token 401 mutes
  // sends until the token changes (re-login), so a dead JWT in localStorage
  // can't 401-spam the console. Renders nothing.
  useEngagementBeacon(true);

  return (
    // F14-PR3: wraps the WHOLE authenticated tree so every billing surface
    // — UpgradeDialogHost below, NavBar's SettingsDialog (EntitlementStatus
    // Block, lazy-loaded there), GuardianHome's per-child entry, ExamEntry
    // Card, and the two new /prep/premium* routes — reads useBilling() from
    // ONE warm fetch instead of each re-fetching cold (preload everything).
    <BillingProvider>
    <div className="App">
      <a href="#main-content" className="skip-nav">Skip to main content</a>
      {/* POST-AIQ-151 COPPA-PR4: the host for the global DOB-capture modal +
          the under-13 limited-mode overlay. Renders nothing until the axios
          interceptor signals a gate, so it's invisible for the common case. */}
      <AgeConsentGate currentUserId={user?.id || null} />
      {/* F14-PR3: the host for the global upgrade dialog. Renders nothing
          until a 402 entitlement_required fires, mirroring AgeConsentGate's
          zero-cost-for-the-common-case shape immediately above. */}
      <UpgradeDialogHost />
      <NavBar user={user} onLogout={onLogout} onUpdateUser={onUpdateUser} />
      {user?.is_email_verified === false && <VerificationBanner />}
      <main id="main-content">
      <Suspense fallback={<div className="page-loading"><Spinner size="md" /> Loading...</div>}>
      <Routes>
        {isTeacher ? (
          <>
            <Route path="/classrooms" element={<RouteErrorBoundary><Classrooms /></RouteErrorBoundary>} />
            <Route path="/dashboard" element={<RouteErrorBoundary><TeacherDashboardPage /></RouteErrorBoundary>} />
            {/* POST-AIQ-151 COPPA-PR4: teacher Career sign-off dashboard. */}
            <Route path="/career-signoff" element={<RouteErrorBoundary><TeacherCareerSignoff /></RouteErrorBoundary>} />
            {/* Task #47 GE-PR4: a teacher who is also a guardian reaches the
                family home here. The GuardianHome owns its own internal tabs. */}
            {isGuardian && <Route path="/family/*" element={<RouteErrorBoundary><GuardianHome user={user} /></RouteErrorBoundary>} />}
            {isAdmin && <Route path="/admin/prep" element={<RouteErrorBoundary><AdminPrep /></RouteErrorBoundary>} />}
            {isAdmin && <Route path="/admin/dashboard" element={<RouteErrorBoundary><AdminDashboardPage /></RouteErrorBoundary>} />}
            {/* ANL-PR4: the founder KPI dashboard (admin-only). */}
            {isAdmin && <Route path="/admin/kpis" element={<RouteErrorBoundary><FounderDashboardPage /></RouteErrorBoundary>} />}
            {/* Task #60: the privacy policy is public, but also reachable while
                signed in so a shared link never dead-ends to a redirect.
                A11y (bug-hunt 2026-07-12): standalone={false} — this mount
                is inside the app shell's own <main id="main-content">, so
                PrivacyPolicy renders a <div> here instead of a second
                <main>. See PrivacyPolicy.js's file-header comment. */}
            <Route path="/privacy" element={<RouteErrorBoundary><PrivacyPolicy standalone={false} /></RouteErrorBoundary>} />
            {/* F12 PR2b: same reasoning as /privacy above — a teacher who is
                also a guardian must be able to resolve an unsubscribe email
                link while signed in, not get redirected past it. */}
            <Route path="/guardian/nudges/unsubscribe" element={<RouteErrorBoundary><UnsubscribePage standalone={false} /></RouteErrorBoundary>} />
            {/* The guardian-invite accept page (see the lazy import's comment):
                declared for every signed-in account so the emailed link never
                dead-ends — the page itself explains when the account can't
                accept. `onLogout` lets a non-guardian sign out to create the
                right account without losing the invite token in the URL. */}
            <Route path="/guardian/accept" element={<RouteErrorBoundary><GuardianAcceptPage user={user} onLogout={onLogout} /></RouteErrorBoundary>} />
            {/* F14-PR3: Stripe's STATIC checkout-cancel/success and portal-
                return redirect paths (config.py, both under /prep/premium)
                must resolve for a signed-in guardian even when their account
                also has is_teacher=true — same reasoning as /privacy and the
                unsubscribe route immediately above. BillingGatedRoute 404-
                redirects while billing_enabled is off. */}
            <Route path="/prep/premium" element={<RouteErrorBoundary><BillingGatedRoute><PremiumHub /></BillingGatedRoute></RouteErrorBoundary>} />
            <Route path="/prep/premium/activating" element={<RouteErrorBoundary><BillingGatedRoute><ActivationPage /></BillingGatedRoute></RouteErrorBoundary>} />
            <Route path="*" element={<Navigate to="/classrooms" replace />} />
          </>
        ) : (
          <>
            <Route path="/learn" element={<RouteErrorBoundary><ExerciseLayout /></RouteErrorBoundary>} />
            {/* F11 PR2: Daily Review — student-only, mirrors /learn's placement.
                Not declared in the teacher branch below (teachers have no
                review queue of their own). */}
            <Route path="/review" element={<RouteErrorBoundary><ReviewSession /></RouteErrorBoundary>} />
            {/* CM-REFERENCE: the standalone concept-refresher card —
                student-only, mirrors /review's placement immediately above
                (teachers have no refreshers of their own either). */}
            <Route path="/reference/:topic/:slug" element={<RouteErrorBoundary><ReferencePage /></RouteErrorBoundary>} />
            <Route path="/chat" element={<RouteErrorBoundary><Chat /></RouteErrorBoundary>} />
            <Route path="/paths" element={<RouteErrorBoundary><LearningPaths user={user} /></RouteErrorBoundary>} />
            <Route path="/prep" element={<RouteErrorBoundary><Prep /></RouteErrorBoundary>} />
            <Route path="/prep/upload" element={<RouteErrorBoundary><StudentPrepUpload /></RouteErrorBoundary>} />
            {/* F10 PR4b: a literal two-segment path, declared ahead of the
                single-param `/prep/:id` below (mirroring `/prep/upload`'s own
                placement) — though React Router's ranking already disambiguates
                these regardless of order (`/prep/:id` only ever matches a
                SINGLE segment after `/prep`, so it structurally can't shadow a
                two-segment path like this one). */}
            <Route path="/prep/practice-sets/:setId" element={<RouteErrorBoundary><PracticeSetRunner /></RouteErrorBoundary>} />
            <Route path="/prep/:id" element={<RouteErrorBoundary><PrepPath /></RouteErrorBoundary>} />
            {/* DSAT-PR6: the full-length exam runs on its own full-screen route
                (focus mode). Deep-linkable + refresh-safe — ExamShell re-mounts
                from server /state at the current position. */}
            <Route path="/prep/:pathId/exam/:sessionId" element={<RouteErrorBoundary><ExamShell /></RouteErrorBoundary>} />
            {/* POST-AIQ-143 PR 7: the Admissions Advisor owns its /advisor/*
                sub-routes internally (chat / shortlist / profile / access),
                so this is a wildcard mount that forwards `user` for the
                guardian/consent UX gating.
                Issue #56: when hidden via Settings, redirect home instead of
                mounting the section — a hidden deep link must not dead-end. */}
            <Route
              path="/advisor/*"
              element={hidden.admissions ? <Navigate to="/learn" replace /> : <RouteErrorBoundary><AdvisorSection user={user} /></RouteErrorBoundary>}
            />
            {/* POST-AIQ-150 CD-PR7: Career Discovery owns its /career/*
                sub-routes internally (matches / check-ins / insights / what-if
                / shortlist / profile / consent / advisor placeholder). Wildcard
                mount forwarding `user` for the guardian-fallback consent id.
                Issue #56: when hidden via Settings, redirect home instead of
                mounting the section — a hidden deep link must not dead-end. */}
            <Route
              path="/career/*"
              element={hidden.career ? <Navigate to="/learn" replace /> : <RouteErrorBoundary><CareerSection user={user} /></RouteErrorBoundary>}
            />
            <Route path="/progress" element={<RouteErrorBoundary><Progress user={user} /></RouteErrorBoundary>} />
            {/* Task #47 GE-PR4: the guardian Family home (child picker + growth
                + admissions + access). Declared for guardians; the verified-link
                + consent gates enforce server-side. GuardianHome owns its tabs. */}
            {isGuardian && <Route path="/family/*" element={<RouteErrorBoundary><GuardianHome user={user} /></RouteErrorBoundary>} />}
            {isAdmin && <Route path="/admin/prep" element={<RouteErrorBoundary><AdminPrep /></RouteErrorBoundary>} />}
            {isAdmin && <Route path="/admin/dashboard" element={<RouteErrorBoundary><AdminDashboardPage /></RouteErrorBoundary>} />}
            {/* ANL-PR4: the founder KPI dashboard (admin-only). */}
            {isAdmin && <Route path="/admin/kpis" element={<RouteErrorBoundary><FounderDashboardPage /></RouteErrorBoundary>} />}
            {/* Task #60: the privacy policy is public, but also reachable while
                signed in so a shared link never dead-ends to a redirect.
                A11y (bug-hunt 2026-07-12): standalone={false} — this mount
                is inside the app shell's own <main id="main-content">, so
                PrivacyPolicy renders a <div> here instead of a second
                <main>. See PrivacyPolicy.js's file-header comment. */}
            <Route path="/privacy" element={<RouteErrorBoundary><PrivacyPolicy standalone={false} /></RouteErrorBoundary>} />
            {/* F12 PR2b: same reasoning as /privacy above — a signed-in
                guardian must be able to resolve an unsubscribe email link
                without getting redirected past it. */}
            <Route path="/guardian/nudges/unsubscribe" element={<RouteErrorBoundary><UnsubscribePage standalone={false} /></RouteErrorBoundary>} />
            {/* The guardian-invite accept page — same dual registration and
                same reasoning as the teacher branch above. */}
            <Route path="/guardian/accept" element={<RouteErrorBoundary><GuardianAcceptPage user={user} onLogout={onLogout} /></RouteErrorBoundary>} />
            {/* F14-PR3: the Premium landing page + the post-checkout
                activation poll. Stripe's STATIC checkout-cancel/success and
                portal-return redirect paths (config.py) both resolve here.
                BillingGatedRoute 404-redirects while billing_enabled is off. */}
            <Route path="/prep/premium" element={<RouteErrorBoundary><BillingGatedRoute><PremiumHub /></BillingGatedRoute></RouteErrorBoundary>} />
            <Route path="/prep/premium/activating" element={<RouteErrorBoundary><BillingGatedRoute><ActivationPage /></BillingGatedRoute></RouteErrorBoundary>} />
            <Route path="*" element={<Navigate to="/learn" replace />} />
          </>
        )}
      </Routes>
      </Suspense>
      </main>
      <footer className="App-footer" role="contentinfo">
        <p>Liang v0.1.0 &middot; Powered by Claude</p>
      </footer>
    </div>
    </BillingProvider>
  );
}

/**
 * The per-account LOCAL wipe — everything on this device that must not
 * survive into the next account on a shared tab: the token pair and user,
 * any stray Authorization default, the in-memory PII caches, the exam
 * outboxes, the pending billing intent and the two advisors' stashed drafts
 * and conversation ids. ONE function so the two paths that end a session —
 * handleLogout (a chosen logout) and the boot-time "expired, nothing to
 * redeem" branch (decideBootAuth) — can never drift apart (Codex audit of
 * PR 1, 2026-10-23: the boot branch used to remove only the token and user,
 * leaving a previous student's advisor draft for whoever signed in next).
 * No network and no React state: callers own those.
 */
export function clearLocalSession() {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  // Defense-in-depth (shared-device boundary): guarantee no Authorization
  // outlives this logout. The request interceptor (setupAxiosAuthHeader.js)
  // derives the header from localStorage, which we just cleared, so this is
  // normally a no-op. But axios flattens `defaults.headers.common` into a
  // request's config BEFORE the request interceptor runs, so any stray
  // default would SHADOW the (now-absent) localStorage token and bleed the
  // previous user's session to the next one. The refresh path no longer
  // writes this default (setupAxiosInterceptor.js), so nothing sets it
  // today — clearing it here keeps the invariant true even if a future
  // change reintroduces a default write.
  delete axios.defaults.headers.common['Authorization'];

  // SECURITY: clear in-memory PII caches so a new user on a shared
  // device can't see the previous user's analytics evidence or
  // student responses.
  resetAnalyticsCache();

  // F28 PR3: the interview track hub's memo (hub payloads carry the
  // caller's own mock in progress; guides are per track) dies with the
  // session — the next user on this device starts from an empty memo.
  clearInterviewCache();

  // ANL-PR3: drop the engagement beacon's learn/prep subject hints so the
  // next user on a shared device can't inherit the prior student's last
  // subject/test-type tag. (The hints are curriculum labels, not PII, but
  // clearing on logout keeps the shared-device wipe complete.)
  clearAreaSubjectHints();

  // R1.1 (privacy, minors, shared devices): purge EVERY exam autosave outbox on
  // the device. Logout doesn't know the active exam session id, and handing a
  // device to another user must leave no queued exam responses behind — so we
  // reap the whole user-scoped, versioned outbox namespace.
  purgeAllExamOutboxes();

  // F14-PR3.1 (review B5): the SAME shared-device wipe discipline as the
  // outbox purge above, for the OTHER cross-session sessionStorage key
  // this app writes (CheckoutFlow.js, right before redirecting to Stripe).
  // Without this, a guardian who starts checkout, then logs out before
  // Stripe redirects back, leaves their pending intent id behind for
  // whoever logs into this tab next (e.g. the student) — ActivationPage
  // would poll a purchase that isn't theirs. try/catch mirrors every
  // other sessionStorage touch in the billing surface (private mode/quota
  // can throw; logout must never fail because of it).
  try {
    sessionStorage.removeItem(PENDING_INTENT_KEY);
  } catch {
    // Best-effort — nothing further depends on this succeeding.
  }

  // UX-3 / PR#538 Sol S1 (HIGH — shared-device minor free-text): the two
  // advisors stash a gate-swallowed DRAFT (the student's own question —
  // potentially sensitive admissions/career text) and their conversation
  // ids in sessionStorage. Same wipe discipline as the outbox/intent
  // purges above: none of the four may survive a logout into the next
  // account on this tab.
  try {
    sessionStorage.removeItem('advisor_draft');
    sessionStorage.removeItem('career_advisor_draft');
    sessionStorage.removeItem('advisor_conversation_id');
    sessionStorage.removeItem('career_advisor_conversation_id');
  } catch {
    // Best-effort — nothing further depends on this succeeding.
  }

  // Platform feedback PR 4 (Codex audit, finding 1): the career check-in
  // runner drafts a student's in-progress ANSWERS to sessionStorage (keyed by
  // user, but a draft namespace left behind on a shared tab is still a
  // previous student's data). Same wipe discipline as the advisors' drafts.
  purgeAssessmentDrafts();
}

/**
 * What a stored session means at boot — see the `bootAuth` state in App.
 *
 *   'ready'      no session, or an access token that is still good (an
 *                unreadable token counts as good: the server decides).
 *   'refreshing' the access token is past its exp and a refresh token exists.
 *   'expired'    past its exp and nothing to redeem: the stale pair is removed
 *                here (there is no refresh token to revoke) so the first paint
 *                is the login page, not a shell that vanishes.
 */
export function decideBootAuth(storedAuth) {
  if (!storedAuth?.token || !storedAuth?.user) return 'ready';
  if (!isAccessTokenExpired(storedAuth.token)) return 'ready';
  let hasRefresh = false;
  try {
    hasRefresh = Boolean(localStorage.getItem('refresh_token'));
  } catch {
    hasRefresh = false;
  }
  if (hasRefresh) return 'refreshing';
  // Nothing to redeem: this session is over, so the WHOLE local wipe runs —
  // the same one a chosen logout performs — not just the token pair. A
  // previous student's stashed advisor draft must not meet the next account
  // on this tab.
  try {
    clearLocalSession();
  } catch {
    // Best-effort: a storage that refuses the remove still boots logged out
    // in memory; the next load re-derives the same verdict.
  }
  return 'expired';
}

function App() {
  // Bug-hunt F1: this is the app's ROOT component, mounted bare in index.js
  // (no ErrorBoundary above it — both branches below now carry their OWN:
  // <AuthenticatedApp>'s and, since the UX-sweep P1 fix (2026-07-12), the
  // logged-out shell's too — see that ErrorBoundary's comment below). A
  // corrupt 'user' value (e.g. a torn write from an interrupted tab close)
  // used to throw synchronously out of JSON.parse here, which was
  // a PERMANENT blank page with no recovery short of manually clearing site
  // data. Guard the read (matching Chat.js / WritingExercise.js's existing
  // try/catch-around-localStorage idiom) and, on a parse failure, clear BOTH
  // 'user' and 'token' — a corrupt user usually means a torn write, so
  // leaving the token behind risks an authenticated shell with no user
  // object. Degrading to a clean logged-out boot is always safe: the login
  // screen is the app's normal unauthenticated state.
  //
  // PR #441 review: `token` and `user` are read TOGETHER through
  // readStoredAuth() — one localStorage pass, one validation — instead of
  // two separate useState initializers. Previously `token`'s initializer
  // ran BEFORE `user`'s (React calls hook initializers in declaration
  // order) and had already captured the about-to-be-orphaned token value
  // from localStorage by the time `user`'s parse failure cleared it — a
  // corrupt-user boot left a stale token alive in React state even though
  // its localStorage copy was gone. Reading both through one function makes
  // that divergence structurally impossible: see readStoredAuth.js.
  const [storedAuth] = useState(readStoredAuth);
  // Boot-time token check (2026-10-21 — tester: "the page loaded and I was
  // already logged in, but then it switched to the login page"). The shell
  // used to paint from a stored access token whose `exp` had already passed;
  // the first request 401'd, the interceptor tried the refresh, and when THAT
  // failed the shell was swapped for Login a second later. Decide up front:
  //   'ready'      — no stored session, or the token is still good.
  //   'refreshing' — expired, but a refresh token exists: run the rotation
  //                  behind a "Signing you back in…" splash (never the shell).
  //   'expired'    — expired and nothing to redeem: boot logged out, no flash.
  // The read is a UX hint only (utils/accessTokenExpiry.js): an unreadable
  // token counts as good and the server stays the authority.
  const [bootAuth, setBootAuth] = useState(() =>
    decideBootAuth(storedAuth)
  );
  const [token, setToken] = useState(bootAuth === 'expired' ? null : storedAuth.token);
  const [user, setUser] = useState(bootAuth === 'expired' ? null : storedAuth.user);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const handleLogin = (accessToken, userData, isNewUser) => {
    setToken(accessToken);
    setUser(userData);
    localStorage.setItem('token', accessToken);
    localStorage.setItem('user', JSON.stringify(userData));
    // Refresh token is persisted by Login.js when it parses the
    // /login or /register response. App.js just sees the access token
    // for in-memory state. Kept this way so the interceptor's source
    // of truth (localStorage.refresh_token) is written exactly once
    // per login event and isn't accidentally overwritten here.
    // Onboarding is the STUDENT welcome (grade + subject picks that seed
    // Learning Paths). A freshly registered guardian has no grade and no
    // learning paths — showing them "What grade are you in?" is a wrong
    // first screen, and on the invite flow it would also stand between
    // them and the accept page the email link pointed at. Skip it for
    // guardian registrations (2026-10-10).
    if (
      isNewUser
      && !localStorage.getItem('onboarding_done')
      && userData?.is_guardian !== true
    ) {
      setShowOnboarding(true);
    }

    // Prefetch learning paths and analytics in background (fire-and-forget).
    // Warms the browser connection cache so Learn and Progress pages load faster.
    //
    // Tag both with `x-background-request` so the global 401 interceptor
    // skips them — a stale-token race here used to silently log out the
    // student mid-session (2026-04-25 reliability fix).
    const prefetchToken = accessToken;
    setTimeout(() => {
      const headers = {
        Authorization: `Bearer ${prefetchToken}`,
        'x-background-request': 'true',
      };
      axios.get(`${API_URL}/api/v1/learning-paths`, { headers }).catch(() => {});

      // Warm the analytics dashboard's server-side SWR cache (perf #75).
      // Extracted to a fire-and-forget util mirroring triggerLoginPrewarm —
      // it carries the SAME `x-background-request` tag so the COPPA
      // age/consent interceptor + the 401 auto-logout both skip it. The
      // first real Progress-page open then hits the warm cache (<5ms)
      // instead of the ~1-2s cold aggregation.
      triggerAnalyticsPrefetch(prefetchToken);

      // Per-user question-bank pre-warm (perf #15/15, originally
      // shipped in PR #100 → rolled back during the 2026-04-26
      // migration outage → reintroduced here). The backend picks the
      // user's most-likely-next node and fills its bank in a
      // background task. Students experience instant questions on
      // their first exercise — no 3-10s AI cold-start. Safe for
      // teachers too (the endpoint just no-ops for them since
      // teachers don't own learning paths).
      triggerLoginPrewarm(prefetchToken);

      // ALSO prefetch the JS chunks for the most-likely routes so the
      // first navigation (Learn / Paths) doesn't stall on bundle
      // download. Runs off the main thread while the login form is
      // fading out. Reported 2026-04-24: pages take too long to load.
      // Teachers skip student chunks, students skip classrooms.
      const isTeacher = userData?.is_teacher === true;
      // Warm RichText's now-lazy chunk (arch Wave 2.1) for EITHER role: it's
      // markdown-guaranteed on the first surface each lands on (student → the
      // Chat/ExerciseView/Learn question text; teacher → ReviewAttemptCard's
      // rendered attempts). Warming it here — off the main thread, while the
      // login form fades — preserves the "instant math" feel the eager static
      // import used to give, without making the light pages pay for it up front.
      warm(_prefetchRichText);
      if (isTeacher) {
        warm(_prefetchClassrooms);
      } else {
        warm(_prefetchChat);
        warm(_prefetchLearningPaths);
        warm(_prefetchExerciseLayout);
      }
    }, 100);
  };

  // UX-13: onboarding hands back the SAVED picks so the cached user object
  // reflects them immediately (Learning Paths personalizes on first visit,
  // not after the next login). Called BARE when the save failed — then
  // nothing merges and the cached object stays honest about server state.
  const handleOnboardingComplete = (gradeLevel, subjects) => {
    setShowOnboarding(false);
    try {
      localStorage.setItem('onboarding_done', 'true');
    } catch {
      // Best-effort mirror (private browsing / quota): a storage failure
      // must never block the in-memory merge below or throw back into the
      // onboarding submit (Sol #549 round 2). Worst case onboarding shows
      // again next boot — annoying, recoverable; a crash here is neither.
    }
    if (gradeLevel !== undefined) {
      handleUpdateUser({ grade_level: gradeLevel, subjects: subjects || [] });
    }
  };

  // Issue #56: shallow-merge a partial into the cached `user` and mirror it to
  // localStorage, exactly as handleLogin persists `user`. Used by the Settings
  // dialog to optimistically reflect a hidden_sections toggle (and to revert on
  // a failed save). Functional update so concurrent merges don't clobber each
  // other; persists the SAME merged object that lands in state.
  const handleUpdateUser = useCallback((partial) => {
    setUser((prev) => {
      const next = { ...(prev || {}), ...partial };
      try {
        localStorage.setItem('user', JSON.stringify(next));
      } catch {
        // Best-effort mirror: if storage is blocked/full, React state must
        // still advance (a throw inside this updater would abort the merge
        // AND escape into whichever caller triggered it — Sol #549 round 2).
        // The mirror heals on the next successful write or login.
      }
      return next;
    });
  }, []);

  const handleLogout = useCallback(() => {
    // POST-AIQ-042 Phase 2 (2026-05-11): best-effort server-side
    // refresh-token revocation BEFORE clearing local state. If the
    // network call fails (offline, server 5xx) we still clear locally
    // — the access token expires on its own in 60 min and the
    // refresh token will expire in 7 days; revoking server-side just
    // accelerates that for the polite case.
    //
    // Tagged x-background-request so the interceptor's auto-refresh
    // path skips it — logging out should never trigger a refresh.
    const refreshTokenToRevoke = localStorage.getItem('refresh_token');
    if (refreshTokenToRevoke) {
      axios
        .post(
          `${API_URL}/api/v1/auth/logout`,
          { refresh_token: refreshTokenToRevoke },
          { headers: { 'x-background-request': 'true' } }
        )
        .catch(() => {
          // Idempotent on the server side; suppressing the error here
          // is intentional. Logging out the user is the user-facing
          // intent — server-side revocation is best-effort.
        });
    }

    setToken(null);
    setUser(null);
    setShowOnboarding(false);
    // The whole per-account local wipe (tokens, user, Authorization default,
    // PII caches, exam outboxes, billing intent, advisor drafts) lives in ONE
    // place, shared with the boot-time expired branch — see clearLocalSession.
    clearLocalSession();
  }, []);

  // Global 401 interceptor — mounted at top level so it's active BEFORE
  // any prefetch fires. Previously this was inside AuthenticatedApp,
  // which mounted AFTER handleLogin kicked off prefetches in setTimeout,
  // leaving users in a broken auth state if those prefetches 401'd.
  //
  // Background-prefetch protection (2026-04-25 reliability fix):
  // requests tagged with `x-background-request` skip the auto-logout.
  // A 401 on a fire-and-forget prefetch should NOT kick the student
  // out of an active session — only foreground requests (the ones the
  // user is actively waiting on) drive logout.
  //
  // Nor does a request that carries its own credential (carriesOwnCredential:
  // a sign-in, a reset or verification link, a reset-link request). Its 401
  // refuses that credential, never the stored session, and signing out over
  // it revoked and removed a session another tab had stored. The error still
  // reaches the form that sent the request.
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && !carriesOwnCredential(error.config)) {
          const isBackground =
            error.config?.headers?.['x-background-request'] === 'true' ||
            error.config?.headers?.['X-Background-Request'] === 'true';
          if (!isBackground) {
            handleLogout();
          }
          // Background 401s: just reject without nuking the session.
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [handleLogout]);

  // POST-AIQ-151 COPPA-PR4: the global age/consent interceptor. Mounted at the
  // top level (like the 401 one) so it's active before any gated request fires
  // — including the post-login prefetches. A 426 age_required → DOB capture +
  // transparent retry; a 403 platform_minor consent_required → the under-13
  // limited-mode overlay. Both are driven through <AgeConsentGate> (mounted in
  // AuthenticatedApp). Registered separately from the 401 handler so each
  // stays single-purpose and independently ejectable.
  useEffect(() => {
    const id = installAgeConsentInterceptor(axios);
    return () => axios.interceptors.response.eject(id);
  }, []);

  // F14-PR3: the billing-upgrade interceptor. Mounted at the top level for
  // the SAME reason as the age/consent one immediately above (active before
  // any gated request fires, including post-login prefetches). A 402
  // entitlement_required -> the shared <UpgradeDialog> (via
  // UpgradeDialogHost, mounted inside AuthenticatedApp); never a retry
  // (buying premium is an out-of-band redirect+webhook flow, not something
  // a transparent retry could resolve inline — see billingUpgradeInterceptor.js).
  useEffect(() => {
    const id = installBillingUpgradeInterceptor(axios);
    return () => axios.interceptors.response.eject(id);
  }, []);

  // The boot-time refresh itself (see `bootAuth` above). 'refreshed' swaps
  // the in-memory token (localStorage already holds the new pair);
  // 'terminal' is a real logout (nothing to redeem — tokens are already
  // cleared by the helper, handleLogout wipes the rest of the per-account
  // state); 'transient' (offline, 5xx) keeps the stored session and lets the
  // interceptor retry on the first 401, which is exactly the pre-existing
  // behaviour. The cancelled flag covers StrictMode's double effect: the
  // second run joins the same single-flight promise.
  useEffect(() => {
    if (bootAuth !== 'refreshing') return undefined;
    let cancelled = false;
    refreshAccessTokenAtBoot().then((result) => {
      if (cancelled) return;
      if (result.status === 'refreshed') {
        setToken(result.token);
      } else if (result.status === 'terminal') {
        handleLogout();
      }
      setBootAuth('ready');
    }).catch(() => {
      // The helper never rejects by contract; if it ever did, the splash
      // must not become permanent — fall back to the pre-existing boot
      // (the shell paints and the interceptor retries on the first 401).
      if (!cancelled) setBootAuth('ready');
    });
    return () => {
      cancelled = true;
    };
  }, [bootAuth, handleLogout]);

  // Suspense fallback for the lazy-loaded auth-flow secondary screens
  // (ResetPassword, VerifyEmail, Onboarding). Tiny — these chunks are
  // small (<10kb each) so the user typically never sees the spinner.
  const lazyAuthFallback = (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--color-page-bg, #FDFBF5)',
      }}
    >
      <Spinner size="md" />
    </div>
  );

  // The boot-time refresh splash: a stored session whose access token has
  // expired is being rotated. Nothing of the shell renders until the
  // outcome is known, so the student never sees the app flash and vanish.
  if (bootAuth === 'refreshing') {
    return (
      <div
        role="status"
        aria-live="polite"
        className="app-boot-refresh"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.75rem',
          background: 'var(--color-page-bg, #FDFBF5)',
          color: 'var(--color-text-secondary, #5e574a)',
        }}
      >
        <Spinner size="md" decorative />
        <span>Signing you back in…</span>
      </div>
    );
  }

  return (
    <BrowserRouter>
      {token && user ? (
        showOnboarding ? (
          // Onboarding is a lazy chunk now — only loaded on the first
          // login after registration. Existing users never download it.
          // Bug-hunt (2026-07-12 hardening pass): this branch had NO
          // ErrorBoundary — a render throw here (a brand-new user's very
          // FIRST screen after registering) used to blank the entire app
          // with no recovery. Wrapped to match the AuthenticatedApp branch
          // below, which already has one.
          <ErrorBoundary>
            <Suspense fallback={lazyAuthFallback}>
              <Onboarding user={user} onComplete={handleOnboardingComplete} />
            </Suspense>
          </ErrorBoundary>
        ) : (
          <ErrorBoundary>
            <AuthenticatedApp
              user={user}
              onLogout={handleLogout}
              onUpdateUser={handleUpdateUser}
            />
          </ErrorBoundary>
        )
      ) : (
        // Bug-hunt P1 defense-in-depth (UX sweep 2026-07-12): the logged-out
        // shell used to mount with NO ErrorBoundary at all — only
        // <AuthenticatedApp> above had one. The concrete trigger (a register
        // 422 whose error.message was a raw pydantic-errors array, rendered
        // as a React child) is fixed at the source in Login.js, but that was
        // a SYMPTOM: any render throw here — Login, ResetPassword,
        // VerifyEmail, or PrivacyPolicy — was previously unrecoverable short
        // of a manual reload, because an uncaught render error unmounts the
        // whole React root. Wrapping the shell mirrors the authenticated
        // branch's existing boundary above and gives every unauthenticated
        // route the same "Something went wrong / Go Home" recovery instead
        // of a blank screen. See App.errorBoundary.test.js.
        <ErrorBoundary>
          {/* Auth-flow lazy chunks (reset-password, verify-email) only
              load when the user clicks a link in their email. The Login
              route stays eager (it's the default first paint). */}
          <Suspense fallback={lazyAuthFallback}>
            <Routes>
              <Route path="/reset-password" element={<RouteErrorBoundary><ResetPassword /></RouteErrorBoundary>} />
              <Route path="/verify-email" element={<RouteErrorBoundary><VerifyEmail /></RouteErrorBoundary>} />
              {/* Task #60: public privacy policy — reachable WITHOUT auth (the
                  login/register footer link + the register-consent link point
                  here). Declared before the catch-all so it resolves instead of
                  falling through to Login.
                  A11y (bug-hunt 2026-07-12): standalone — no app shell renders
                  on this unauthenticated side, so PrivacyPolicy must own the
                  <main> landmark itself here. */}
              <Route path="/privacy" element={<RouteErrorBoundary><PrivacyPolicy standalone /></RouteErrorBoundary>} />
              {/* F12 PR2b: the public unsubscribe landing page — the page a
                  guardian's email link opens. No app shell renders on this
                  branch, so it must own the <main> landmark itself
                  (standalone, PrivacyPolicy's exact mechanism). Declared
                  before the catch-all so it resolves instead of falling
                  through to Login. */}
              <Route path="/guardian/nudges/unsubscribe" element={<RouteErrorBoundary><UnsubscribePage standalone /></RouteErrorBoundary>} />
              <Route path="*" element={<Login onLogin={handleLogin} />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      )}
    </BrowserRouter>
  );
}

export default App;
